import { apiEndpoint } from '../config/api';
import { apiFetch } from './api';

async function parseResponse(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || `Request failed with status ${response.status}`);
  }
  return body;
}

// POST /franchise-eligibility-reject -- reject an ACTIVE eligibility proposal.
// payload: { enablement_period_id }. reviewed_by is derived server-side from the authenticated
// user (never sent from the client). Returns { message, period }. Throws on non-2xx -- e.g.
// 401 (unauthenticated), 404 (missing), 409 (not an active proposal).
export async function rejectEligibilityPeriod(enablementPeriodId) {
  const response = await apiFetch(`${apiEndpoint}/franchise-eligibility-reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enablement_period_id: enablementPeriodId }),
  });
  return parseResponse(response);
}

// POST /franchise-eligibility-confirm -- confirm an active proposal (Confirm / Adjust & Confirm).
// dates are stored HALF-OPEN 'YYYY-MM-DD' (enabledThroughDate null => open-ended). reviewed_by is
// derived server-side from the authenticated user. Returns either { committed: true, period, impact }
// or, when accepted LK/SI seasons would fall outside eligibility and acknowledged is not set,
// { committed: false, requiresAcknowledgment: true, impact: { count, seasons } }. Resubmit with
// acknowledged: true to commit. Throws on non-2xx (401 / 404 / 409 / 400).
export async function confirmEligibilityPeriod(
  enablementPeriodId,
  { enabledFromDate, enabledThroughDate, acknowledged = false }
) {
  const response = await apiFetch(`${apiEndpoint}/franchise-eligibility-confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      enablement_period_id: enablementPeriodId,
      enabled_from_date: enabledFromDate,
      enabled_through_date: enabledThroughDate,
      acknowledged,
    }),
  });
  return parseResponse(response);
}

// GET /franchise-eligibility-candidates -- LK/SI franchises (from Dash) with no eligibility-period
// row yet, each with a readiness summary. Returns { candidates: [...] }. Read-only.
export async function getEligibilityCandidates() {
  const response = await apiFetch(`${apiEndpoint}/franchise-eligibility-candidates`);
  return parseResponse(response);
}

// POST /franchise-eligibility-create -- create the FIRST eligibility period for a franchise, born
// authoritative (confirmed, source=manual). reviewed_by/created_by derived server-side from the
// authenticated user. Same half-open date contract and Option-B acknowledgment shape as confirm/edit.
export async function createEligibilityPeriod(
  comCode,
  { enabledFromDate, enabledThroughDate, acknowledged = false }
) {
  const response = await apiFetch(`${apiEndpoint}/franchise-eligibility-create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      com_code: comCode,
      enabled_from_date: enabledFromDate,
      enabled_through_date: enabledThroughDate,
      acknowledged,
    }),
  });
  return parseResponse(response);
}

// POST /franchise-eligibility-delete -- HARD-DELETE a manually created eligibility period. Only
// source='manual' rows are deletable (server-enforced); the exact enablement_period_id is targeted.
// Returns { message, com_code, ... }. Throws on non-2xx -- 400 (bad id), 404 (missing), 409 (not a
// manual row / stale). Deleting a franchise's only period returns it to fail-open and re-lists it as
// an Add Eligibility Period candidate.
export async function deleteEligibilityPeriod(enablementPeriodId) {
  const response = await apiFetch(`${apiEndpoint}/franchise-eligibility-delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enablement_period_id: enablementPeriodId }),
  });
  return parseResponse(response);
}

// POST /franchise-eligibility-edit -- edit the dates of an active CONFIRMED period. Not a
// re-confirmation: reviewed_by/reviewed_at are preserved server-side; updated_by/updated_at record
// the editor. Same half-open date contract and Option-B acknowledgment shape as confirm: returns
// { committed: true, period, impact } or { committed: false, requiresAcknowledgment: true, impact }.
export async function editEligibilityPeriod(
  enablementPeriodId,
  { enabledFromDate, enabledThroughDate, acknowledged = false }
) {
  const response = await apiFetch(`${apiEndpoint}/franchise-eligibility-edit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      enablement_period_id: enablementPeriodId,
      enabled_from_date: enabledFromDate,
      enabled_through_date: enabledThroughDate,
      acknowledged,
    }),
  });
  return parseResponse(response);
}
